/**
 * CENTRALIZED COMPANY CONSTANTS
 * 
 * Deze file bevat alle herbruikbare company data die op MEERDERE pagina's wordt gebruikt.
 * 
 * WAAROM CENTRAAL?
 * - Als cijfers veranderen (bijv. 3.000+ klanten), hoef je maar 1x aan te passen
 * - Consistency: overal dezelfde cijfers
 * - DRY principle: Don't Repeat Yourself
 * 
 * GEBRUIK:
 * Import waar nodig: import { COMPANY_STATS, COMPANY_INFO } from '@/lib/company-constants';
 */

// ============================================================================
// COMPANY INFO (BASIS DATA)
// ============================================================================

import googleReviewsData from './google-reviews.data.json';

export const COMPANY_INFO = {
  name: 'Jamezz',
  founded: '2017',
  headquartersCity: 'Breda',
  headquartersCountry: 'Netherlands',
  email: {
    support: 'info@jamezz.com',
    sales: 'sales@jamezz.com',
  },
  phone: {
    main: '+31 85 301 3000',
  },
} as const;

// ============================================================================
// COMPANY STATS (TRUST SIGNALS)
// ============================================================================
// Deze cijfers worden gebruikt op ALLE solution pages (Kiosk, QR, Tablet, Webshop)
// Om te updaten: pas hier aan, rebuild, en alle pagina's zijn geupdate!

export const COMPANY_STATS = {
  activeCustomers: {
    value: '2.000+',
    // NL/EN/DE labels komen uit JSON (per taal specifiek)
  },
  countries: {
    value: '25+',
    // Labels komen uit JSON
  },
  ordersPerYear: {
    value: '25M+',
    // Labels komen uit JSON
  },
  yearsSinceFoundation: {
    value: COMPANY_INFO.founded, // Just "2017", label "Sinds/Since/Seit" comes from JSON
    // Labels komen uit JSON
  },
} as const;

// ============================================================================
// GOOGLE REVIEWS DATA
// ============================================================================
// SINGLE SOURCE OF TRUTH: de rating + het aantal komen uit
// `lib/google-reviews.data.json`. Wijzig NOOIT de cijfers hier — pas het JSON-
// bestand aan (of run `npm run sync:reviews` om ze live uit Google op te halen).
// Deze constante levert de getallen aan structured data (schema.org).
// Voor locale-geformatteerde UI-weergave: gebruik `lib/google-reviews.ts`.

export const GOOGLE_REVIEWS = {
  // Numeric values - for schema.org (always use these in structured data)
  rating: googleReviewsData.rating,           // Google rating (1-5) as NUMBER
  reviewCount: googleReviewsData.reviewCount,  // Total reviews as NUMBER
  bestRating: 5,                               // Max rating as NUMBER
  worstRating: 1,                              // Min rating as NUMBER

  // String versions - for UI display only (locale-neutral fallback)
  ratingString: String(googleReviewsData.rating),
  reviewCountString: String(googleReviewsData.reviewCount),
} as const;

// ============================================================================
// EXPERTISE STATS GENERATOR
// ============================================================================
// Deze functie genereert de stats array voor ExpertiseStatsSection
// Gebruik dit in je JSON files door de values te refereren

/**
 * Genereert stats array voor ExpertiseStatsSection component
 * 
 * @param labels - Vertaalde labels per stat (komt uit JSON per taal)
 * @returns Stats array ready for ExpertiseStatsSection
 * 
 * @example
 * // In je page component:
 * const stats = generateExpertiseStats({
 *   activeCustomers: messages.stats.activeCustomers,
 *   yearsSinceFoundation: messages.stats.yearsSinceFoundation,
 *   ordersPerYear: messages.stats.ordersPerYear,
 *   countries: messages.stats.countries,
 * });
 */
export function generateExpertiseStats(labels: {
  activeCustomers: string;
  yearsSinceFoundation: string;
  ordersPerYear: string;
  countries: string;
}) {
  return [
    {
      value: COMPANY_STATS.activeCustomers.value,
      label: labels.activeCustomers,
    },
    {
      value: COMPANY_STATS.yearsSinceFoundation.value,
      label: labels.yearsSinceFoundation,
    },
    {
      value: COMPANY_STATS.ordersPerYear.value,
      label: labels.ordersPerYear,
    },
    {
      value: COMPANY_STATS.countries.value,
      label: labels.countries,
    },
  ];
}

// ============================================================================
// QUOTABLE STATEMENT TEMPLATES
// ============================================================================
// Template voor AI-citeerbare statements (gebruikt op alle solution pages)

export const QUOTABLE_STATEMENT_TEMPLATE = {
  // Deze wordt in JSON ingevuld met solution-specifieke termen
  // Bijv. "kiosken" voor Kiosk pagina, "QR-bestellen" voor QR pagina
  structure: '[SOLUTION] wordt dagelijks vertrouwd door [VENUES] in [COUNTRIES] voor [BENEFITS] met automatische POS-integratie.',
} as const;

